import "./MyActivity2.css";
import React, { useState } from "react";
import ChevronRightIcon from "../../assets/myactivity2/task_badges/ChevronRightIcon.svg";
import { useSelector } from "react-redux";
import {
  useGetUserActivityStatsQuery,
  useGetUserTaskBasedBadgesQuery,
  useGetUserPointBasedBadgesQuery,
  useGetUserBookBasedBadgesQuery,
  useGetUserTimeTrialBasedBadgesQuery,
} from "../../redux-toolkit/features/my-activity/myActivityApiSlice";

// Journey Components
import {
  TasksJourney,
  PointsJourney,
  BooksJourney,
  TimeTrialsJourney,
  CoursesJourney,
} from "./journeys";

const MyActivity2 = () => {
  const { user } = useSelector((state) => state.auth);
  const theme = useSelector((state) => state.theme);

  const {
    data: userStats,
    isLoading,
    error,
  } = useGetUserActivityStatsQuery(user?._id);

  const {
    data: taskBadgesData,
    isLoading: taskBadgesLoading,
    error: taskBadgesError,
  } = useGetUserTaskBasedBadgesQuery(user?._id);

  const {
    data: pointBadgesData,
    isLoading: pointBadgesLoading,
    error: pointBadgesError,
  } = useGetUserPointBasedBadgesQuery(user?._id);

  const {
    data: bookBadgesData,
    isLoading: bookBadgesLoading,
    error: bookBadgesError,
  } = useGetUserBookBasedBadgesQuery(user?._id);

  const {
    data: timeTrialBadgesData,
    isLoading: timeTrialBadgesLoading,
    error: timeTrialBadgesError,
  } = useGetUserTimeTrialBasedBadgesQuery(user?._id);

  const [activeTab, setActiveTab] = useState(0);

  // Get current progress based on active tab
  const getCurrentProgress = () => {
    switch (activeTab) {
      case 0: // Tasks Completed - use task badges API for more accurate count
        return (
          taskBadgesData?.data?.totalTasksCompleted ||
          userStats?.data?.completedTasksCount ||
          0
        );
      case 1: // Points Earned - use point badges API for more accurate count
        return (
          pointBadgesData?.data?.totalPointsEarned ||
          userStats?.data?.pointsEarned ||
          0
        );
      case 2: // Books Completed - use book badges API for more accurate count
        return (
          bookBadgesData?.data?.totalBooksCompleted ||
          userStats?.data?.booksCompleted ||
          0
        );
      case 3: // Time Trials Completed - use time trial badges API for more accurate count
        return (
          timeTrialBadgesData?.data?.totalTimeTrialsCompleted ||
          userStats?.data?.timeTrialsCompleted ||
          0
        );
      case 4: // Courses Completed
        return userStats?.data?.coursesCompleted || 0;
      default:
        return (
          taskBadgesData?.data?.totalTasksCompleted ||
          userStats?.data?.completedTasksCount ||
          0
        );
    }
  };

  const currentProgress = getCurrentProgress();

  const statsData = [
    {
      id: 1,
      title: "Tasks Completed",
      value: (
        taskBadgesData?.data?.totalTasksCompleted ||
        userStats?.data?.completedTasksCount ||
        0
      ).toString(),
      icon: ChevronRightIcon,
    },
    {
      id: 2,
      title: "Points Earned",
      value: (
        pointBadgesData?.data?.totalPointsEarned ||
        userStats?.data?.pointsEarned ||
        0
      ).toString(),
      icon: ChevronRightIcon,
    },
    {
      id: 3,
      title: "Books Completed",
      value: (
        bookBadgesData?.data?.totalBooksCompleted ||
        userStats?.data?.booksCompleted ||
        0
      ).toString(),
      icon: ChevronRightIcon,
    },
    {
      id: 4,
      title: "Time Trials Completed",
      value: (
        timeTrialBadgesData?.data?.totalTimeTrialsCompleted ||
        userStats?.data?.timeTrialsCompleted ||
        0
      ).toString(),
      icon: ChevronRightIcon,
    },
    {
      id: 5,
      title: "Courses Completed",
      value: userStats?.data?.coursesCompleted?.toString() || "0",
      icon: ChevronRightIcon,
    },
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  // Loading state
  if (
    isLoading ||
    taskBadgesLoading ||
    pointBadgesLoading ||
    bookBadgesLoading ||
    timeTrialBadgesLoading
  ) {
    return (
      <div
        className={`myactivity2-container ${
          theme.mode === "light" ? "light" : "dark"
        }`}
      >
        {/* <div className="welcome-section">
          <h2 className="welcome-text">
            Welcome back {user?.fname || "User"}!
          </h2>
        </div> */}
        <div className="myactivity2-content">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
              color: "#fff",
              fontSize: "1.2rem",
            }}
          >
            Loading your activity data...
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (
    error ||
    taskBadgesError ||
    pointBadgesError ||
    bookBadgesError ||
    timeTrialBadgesError
  ) {
    return (
      <div
        className={`myactivity2-container ${
          theme.mode === "light" ? "light" : "dark"
        }`}
      >
        {/* <div className="welcome-section">
          <h2 className="welcome-text">
            Welcome back {user?.fname || "User"}!
          </h2>
        </div> */}
        <div className="myactivity2-content">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
              color: "#ff6b6b",
              fontSize: "1.2rem",
            }}
          >
            Error loading activity data. Please try again later.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`myactivity2-container ${
        theme.mode === "light" ? "light" : "dark"
      }`}
      style={{
        backgroundImage: `url(${theme.bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundAttachment: "fixed",
      }}
    >
      {/* <div className="welcome-section">
        <h2 className="welcome-text">Welcome back {user?.fname || "User"}!</h2>
      </div> */}

      <div
        className={`myactivity2-content ${
          activeTab === 2 ? "books-tab-active" : ""
        }`}
      >
        {/* Left Panel - Activity Statistics */}
        <div className="left-panel">
          <div className="stats-tabs">
            {statsData.map((stat, index) => (
              <div
                key={stat.id}
                className={`stat-card secondary ${
                  activeTab === index ? "active" : ""
                }`}
                onClick={() => handleTabClick(index)}
              >
                <div className="stat-content">
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-title">{stat.title}</div>
                </div>
                {activeTab === index && (
                  <div className="stat-icon">
                    <img
                      src={stat.icon}
                      alt="chevron right"
                      style={{
                        width: "24px",
                        height: "24px",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Journey Display */}
        <div className="right-panel">
          {activeTab === 0 && (
            <TasksJourney currentProgress={currentProgress} />
          )}
          {activeTab === 1 && (
            <PointsJourney currentProgress={currentProgress} />
          )}
          {activeTab === 2 && (
            <BooksJourney currentProgress={currentProgress} />
          )}
          {activeTab === 3 && (
            <TimeTrialsJourney currentProgress={currentProgress} />
          )}
          {activeTab === 4 && (
            <CoursesJourney currentProgress={currentProgress} />
          )}
        </div>
      </div>
    </div>
  );
};

export default MyActivity2;
